import { getScrollContainer } from '@theme/scroll-container';

const ENTRY_PARAM = '_bb_entry';
const RETURN_PARAM = '_bb_return';
const STORAGE_PREFIX = 'bunny-breadcrumb:';
const HISTORY_KEY = 'bunnyBreadcrumbEntry';
const MAX_AGE = 30 * 60 * 1000;

function isFresh(record) {
  const age = Date.now() - record?.createdAt;
  return record?.version === 1 && Number.isFinite(age) && age >= 0 && age < MAX_AGE;
}

function storeRecord(kind, record) {
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (!key.startsWith(STORAGE_PREFIX)) continue;
      try {
        if (isFresh(JSON.parse(sessionStorage.getItem(key)))) continue;
      } catch {}
      sessionStorage.removeItem(key);
    }
    const token = crypto.randomUUID();
    sessionStorage.setItem(`${STORAGE_PREFIX}${kind}:${token}`, JSON.stringify(record));
    return token;
  } catch {
    return null;
  }
}

function takeRecord(kind, token) {
  if (!/^[a-f0-9-]{36}$/i.test(token ?? '')) return null;
  try {
    const key = `${STORAGE_PREFIX}${kind}:${token}`;
    const value = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    const record = JSON.parse(value);
    return isFresh(record) ? record : null;
  } catch {
    return null;
  }
}

function localURL(value) {
  try {
    if (typeof value !== 'string' || !value) return null;
    const url = new URL(value, location.origin);
    return url.origin === location.origin ? url : null;
  } catch {
    return null;
  }
}

function cleanURL() {
  const url = new URL(location.href);
  url.searchParams.delete(ENTRY_PARAM);
  url.searchParams.delete(RETURN_PARAM);
  if (url.href !== location.href) history.replaceState(history.state, '', url);
  return url;
}

function ordinaryClick(event) {
  return !event.defaultPrevented && event.button === 0 &&
    !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;
}

function temporaryLink(link, url) {
  const original = link.getAttribute('href');
  link.href = url.href;
  setTimeout(() => {
    if (link.href === url.href) link.setAttribute('href', original);
  }, 0);
}

function recordProductEntry(event, breadcrumbs) {
  if (!ordinaryClick(event) || !(event.target instanceof Element)) return;
  if (event.target.closest('button, input, label, select, summary, [role="button"], [tabindex="1"]')) return;
  const card = event.target.closest('product-card:not([data-no-navigation])');
  const row = card?.closest('.product-grid__item[data-product-id][data-page]');
  if (!row?.closest('#MainContent results-list')) return;
  const link = event.target.closest('a[href]') ?? card.querySelector('a[ref="productCardLink"]');
  if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
  const destination = localURL(link.href);
  const collectionURL = localURL(breadcrumbs.dataset.collectionUrl);
  if (!destination || !/\/products\/[^/]+\/?$/.test(destination.pathname) || !collectionURL) return;
  if (location.pathname !== collectionURL.pathname) return;
  const page = Number(row.dataset.page);
  if (!Number.isSafeInteger(page) || page < 1) return;
  const source = new URL(location.href);
  source.hash = '';
  source.searchParams.delete(ENTRY_PARAM);
  source.searchParams.delete(RETURN_PARAM);
  source.searchParams.set('page', String(page));
  const token = storeRecord('entry', {
    version: 1,
    createdAt: Date.now(),
    productId: row.dataset.productId,
    productPath: destination.pathname,
    collectionId: breadcrumbs.dataset.collectionId,
    collectionPath: collectionURL.pathname,
    returnURL: source.href,
  });
  if (!token) return;
  destination.searchParams.set(ENTRY_PARAM, token);
  temporaryLink(link, destination);
}

function validProductEntry(record, breadcrumbs, leaf) {
  const collectionURL = localURL(leaf?.href);
  const returnURL = localURL(record?.returnURL);
  const productURL = localURL(breadcrumbs.dataset.productUrl);
  return isFresh(record) && collectionURL && returnURL && productURL &&
    record.productId === breadcrumbs.dataset.productId && record.productPath === productURL.pathname &&
    record.productPath === location.pathname && record.collectionId === leaf.dataset.collectionId &&
    record.collectionPath === collectionURL.pathname && returnURL.pathname === collectionURL.pathname;
}

function initializeProduct(breadcrumbs) {
  const token = new URL(location.href).searchParams.get(ENTRY_PARAM);
  const incoming = token ? takeRecord('entry', token) : history.state?.[HISTORY_KEY];
  cleanURL();
  const leaf = breadcrumbs.querySelector('[data-breadcrumb-leaf]');
  const record = validProductEntry(incoming, breadcrumbs, leaf) ? incoming : null;
  const state = typeof history.state === 'object' && history.state !== null ? { ...history.state } : {};
  delete state[HISTORY_KEY];
  if (record) state[HISTORY_KEY] = record;
  history.replaceState(state, '');
  if (!record) return;
  leaf.addEventListener('click', (event) => {
    if (!ordinaryClick(event) || !validProductEntry(record, breadcrumbs, leaf)) return;
    const returnToken = storeRecord('return', record);
    if (!returnToken) return;
    const url = new URL(record.returnURL);
    url.searchParams.set(RETURN_PARAM, returnToken);
    temporaryLink(leaf, url);
  });
}

function restoreCard(record, breadcrumbs) {
  const row = [...document.querySelectorAll('#MainContent results-list .product-grid__item[data-product-id]')]
    .find((element) => element.dataset.productId === record.productId);
  if (!row) {
    location.replace(breadcrumbs.dataset.collectionUrl);
    return;
  }
  const container = getScrollContainer();
  const viewportTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
  const headerBottom = document.querySelector('#header-group')?.getBoundingClientRect().bottom ?? viewportTop;
  const offset = Math.max(0, headerBottom - viewportTop) + 16;
  const top = container.scrollTop + row.getBoundingClientRect().top - viewportTop - offset;
  container.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  row.querySelector('a[href]')?.focus({ preventScroll: true });
  row.classList.add('bunny-breadcrumb-target');
  setTimeout(() => row.classList.remove('bunny-breadcrumb-target'), 1800);
}

function initializeCollection(breadcrumbs) {
  const token = new URL(location.href).searchParams.get(RETURN_PARAM);
  const record = takeRecord('return', token);
  const currentURL = cleanURL();
  const collectionURL = localURL(breadcrumbs.dataset.collectionUrl);
  const returnURL = localURL(record?.returnURL);
  if (record && collectionURL && returnURL && record.collectionId === breadcrumbs.dataset.collectionId &&
    record.collectionPath === collectionURL.pathname && currentURL.pathname === collectionURL.pathname &&
    currentURL.href === returnURL.href) {
    const restore = () => requestAnimationFrame(() => requestAnimationFrame(() => restoreCard(record, breadcrumbs)));
    if (document.readyState === 'complete') restore();
    else window.addEventListener('pageshow', restore, { once: true });
  }
  document.addEventListener('click', (event) => recordProductEntry(event, breadcrumbs), { capture: true });
}

function initialize() {
  const breadcrumbs = document.querySelector('bunny-breadcrumbs');
  if (!breadcrumbs || window.Shopify?.designMode) return;
  if (breadcrumbs.dataset.pageType === 'product') initializeProduct(breadcrumbs);
  if (breadcrumbs.dataset.pageType === 'collection') initializeCollection(breadcrumbs);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
else initialize();
